/** German landing copy — switch via LanguageSwitcher */
export const hero = {
  eyebrow: 'AI Trading Terminal · Multi-Asset',
  headline: ['Jeder Markt.', 'Ein Terminal.'],
  subheadline:
    'Multi-Timeframe-Signale, Liquidations-Heatmap, AI-Market-Brain und Forecast — alles in einer schnellen Web-App. Kein Tab-Hopping, kein API-Key-Chaos im Browser.',
  trustLine: 'Keine Kreditkarte · Free-Plan dauerhaft · Jederzeit upgraden',
  primaryCta: 'Kostenlos starten',
  secondaryCta: 'So funktioniert es',
  secondaryAnchor: '#how-it-works',
};

export const trustStats = [
  { value: 14, suffix: '', label: 'Indikatoren' },
  { value: 4, suffix: '', label: 'Plan-Stufen' },
  { value: 8, suffix: '+', label: 'Datenquellen' },
  { value: 5, suffix: '', label: 'AI-Layer' },
];

export const dataProviders = [
  'Binance',
  'Bybit',
  'OKX',
  'Coinbase',
  'Kraken',
  'Yahoo Finance',
  'CoinGlass',
  'Etherscan',
];

export const productOverview = {
  eyebrow: 'Was ist MarketWatcher?',
  headline: ['Kontext statt Noise.', 'Signale mit Substanz.'],
  description:
    'MarketWatcher ist ein browserbasiertes Trading-Terminal für Krypto und Aktien. Es kombiniert technische Analyse, Orderflow-Daten und AI-gestützte Markteinschätzungen in einer einheitlichen Oberfläche.',
  bullets: [
    'Multi-Timeframe-Signale mit Confidence-Score',
    'Server-seitige AI-Keys — sicher, keine Browser-Exposure',
    'Liquidations-Heatmap & Orderflow live im Chart',
    'Free-Plan ohne Zeitlimit — sofort loslegen',
  ],
};

export const howItWorks = {
  eyebrow: 'So funktioniert es',
  headline: ['Drei Schritte.', 'Ein klarer Workflow.'],
  steps: [
    {
      num: '01',
      title: 'Markt beobachten',
      description:
        'Wähle deine Ticker, setze Watchlists und Alerts. Overview, Charts und News-Feed liefern den Überblick.',
    },
    {
      num: '02',
      title: 'Signale analysieren',
      description:
        'Multi-Timeframe-Confluence, Indikatoren und AI-Market-Brain liefern strukturierte Einschätzungen.',
    },
    {
      num: '03',
      title: 'Entscheidung treffen',
      description:
        'Forecast, Heatmap und Trade-Journal helfen dir, Setups zu validieren und deinen Prozess zu verbessern.',
    },
  ],
};

export const whySection = {
  eyebrow: 'Warum MarketWatcher?',
  headline: ['Mehr Kontext.', 'Bessere Entscheidungen.'],
  items: [
    {
      title: 'Kontext statt Noise',
      description: 'Strukturierte Signale mit Confidence-Score und MTF-Bestätigung.',
      tone: 'accent',
    },
    {
      title: 'Sichere AI-Integration',
      description: 'OpenAI, Anthropic und Groq laufen über unseren Backend-Proxy.',
      tone: 'success',
    },
    {
      title: 'Multi-Asset aus einer Hand',
      description: 'BTC, ETH, Top-Alts und Aktien — ein Terminal für alle Märkte.',
      tone: 'warn',
    },
    {
      title: 'Fair skaliert',
      description: 'Vom kostenlosen Einstieg bis Terminal — nur für das zahlen, was du brauchst.',
      tone: 'accent',
    },
  ],
};

export const trustSecurity = {
  eyebrow: 'Vertrauen & Sicherheit',
  headline: ['Deine Keys bleiben', 'auf unseren Servern.'],
  description: 'MarketWatcher ist für Trader gebaut, die Wert auf sichere AI-Nutzung legen.',
  items: [
    {
      title: 'Server-seitige AI-Keys',
      description: 'LLM-Anfragen laufen über unseren Backend-Proxy — keine Keys im Browser.',
    },
    {
      title: 'Plan-basierte Quotas',
      description: 'Tägliche AI-Limits pro Plan verhindern Missbrauch.',
    },
    {
      title: 'HttpOnly Session-Cookies',
      description: 'Authentifizierung über sichere, server-verwaltete Sessions.',
    },
    {
      title: 'Keine Anlageberatung',
      description: 'Wir liefern Marktdaten und Tools. Trading birgt erhebliche Risiken.',
    },
  ],
};

export const useCases = {
  eyebrow: 'Zielgruppen',
  headline: ['Für jeden Trading-Stil.', 'Den passenden Plan.'],
  personas: [
    {
      title: 'Swing-Trader',
      pain: 'Zu viele Tabs, zu wenig MTF-Überblick.',
      solution: 'MTF-Matrix und strukturierte Signale in einer App.',
      plan: 'Core',
    },
    {
      title: 'Day-Trader',
      pain: 'Liquidations-Cluster fehlen im Standard-Charting.',
      solution: 'Heatmap-Overlay und Futures-Panel im Pro-Plan.',
      plan: 'Pro',
    },
    {
      title: 'Analyst',
      pain: 'AI ohne sichere Key-Verwaltung.',
      solution: 'AI-Market-Brain mit Server-Proxy und Quotas.',
      plan: 'Pro',
    },
    {
      title: 'Teams & Power-User',
      pain: 'Einzelmonitor reicht nicht für parallele Workflows.',
      solution: 'Multi-Monitor Workspaces und Prioritäts-Signale.',
      plan: 'Terminal',
    },
  ],
};

export const coreFeatures = {
  eyebrow: 'Core Features',
  headline: ['Alles für den', 'modernen Trader.'],
  description: 'Von MTF-Signalen bis Liquidity-Heatmap — ohne Tab-Hopping.',
  items: [
    {
      title: 'Multi-Timeframe-Signale',
      description: 'Composite-Signale mit MTF-Bestätigung über 1m bis 1d.',
      bullets: ['14 Indikatoren', 'Pattern + Struktur', 'Adaptive Weights'],
      emoji: '◇',
      tone: 'accent',
    },
    {
      title: 'AI-Market-Brain',
      description: 'OpenAI / Anthropic / Groq als Backend-Proxy.',
      bullets: ['Server-seitige Keys', 'Plan-Quota', 'Multi-Provider'],
      emoji: '◈',
      tone: 'success',
    },
    {
      title: 'Liquidations-Heatmap',
      description: 'CoinGlass + synthetische Cluster + Live-WS-Feed.',
      bullets: ['Live-WS', 'Synthetic-Cluster', 'Chart-Overlay'],
      emoji: '◉',
      tone: 'warn',
    },
    {
      title: 'Forecast + CandlePredict',
      description: 'Linreg-Forecast und Monte-Carlo-Pfade im Chart.',
      bullets: ['Linreg', 'Monte-Carlo', 'OHLC-Synth'],
      emoji: '◬',
      tone: 'accent',
    },
    {
      title: 'Multi-Asset-Pulse',
      description: 'Overview-Center mit Pulse-Strip und MTF-Matrix.',
      bullets: ['Pulse-Strip', 'MTF-Matrix', 'Community-Vote'],
      emoji: '◐',
      tone: 'success',
    },
    {
      title: 'Wallet + Portfolio',
      description: 'EVM- & BTC-Wallet, Paper-Trading mit Long/Short.',
      bullets: ['EVM + BTC', 'Long/Short', 'Trade-Journal'],
      emoji: '◆',
      tone: 'danger',
    },
  ],
};

export const pricingSection = {
  eyebrow: 'Preise',
  headline: ['Faire Pläne.', 'Klare Limits.'],
  description: 'Vom Free-Plan bis Terminal — jederzeit wechselbar.',
};

export const faqHome = [
  {
    q: 'Brauche ich einen Account?',
    a: 'Ja. Registrierung ist kostenlos (~30 Sekunden). Free-Plan sofort verfügbar.',
  },
  {
    q: 'Was ist im Free-Plan enthalten?',
    a: 'Basis-Charts, 3 Ticker, 1 Watchlist, 2 Alerts, News-Feed — ohne Zeitlimit.',
  },
  {
    q: 'Wie funktioniert AI-Market-Brain?',
    a: 'AI-Anfragen über Backend-Proxy. Keys bleiben auf dem Server.',
  },
  {
    q: 'Kann ich jederzeit upgraden?',
    a: 'Ja. Plan-Wechsel im Account jederzeit möglich.',
  },
  {
    q: 'Welche Zahlungsmethoden?',
    a: 'Krypto verfügbar. Karten in Vorbereitung. Preise in EUR.',
  },
  {
    q: 'Ist das Anlageberatung?',
    a: 'Nein. Keine individuelle Anlageberatung. Trading birgt Risiken.',
  },
];

export const finalCta = {
  headline: ['Trade mit dem Kontext,', 'den die meisten übersehen.'],
  subtitle: 'Free-Account in 30 Sekunden.',
  primaryCta: 'Kostenlos starten',
  secondaryCta: 'Preise ansehen',
  secondaryAnchor: '#pricing',
};

export const nav = {
  features: 'Features',
  pricing: 'Preise',
  faq: 'FAQ',
};

export const common = {
  demo: 'DEMO',
  viewAllFeatures: 'Alle Features im Detail →',
  viewAllFaq: 'Alle Fragen ansehen →',
  viewPricingDetail: 'Alle Limits & Features im Detail →',
  loadingPlans: 'Lade Pläne …',
  fallbackPlans: 'Pläne aus statischem Snapshot — Backend nicht erreichbar.',
};
